// src/components/GlobalDashboardButton.tsx
// Dashboard drawer component that can be triggered from sidebar or floating button
import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Fab,
  Drawer,
  IconButton,
  useTheme,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeartOutlined";
import "./GlobalDashboardButton.scss";
import { DASHBOARDS } from "../services/constants";

interface ButtonPosition {
  x: number;
  y: number;
}

interface GlobalDashboardButtonProps {
  isOpen?: boolean;
  onClose?: () => void;
  showFloatingButton?: boolean;
}

const DEFAULT_POSITION = { x: 24, y: 24 }; // 24px from right, 24px from bottom

// Get Grafana base URL from environment variable
const GRAFANA_BASE_URL = process.env.REACT_APP_GRAFANA_BASE_URL || "";

const GlobalDashboardButton: React.FC<GlobalDashboardButtonProps> = ({
  isOpen = false,
  onClose,
  showFloatingButton = false,
}) => {
  const theme = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = useState(isOpen);
  const [buttonPosition, setButtonPosition] =
    useState<ButtonPosition>(DEFAULT_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [hasMoved, setHasMoved] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // Track active dashboard tab

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragThreshold = 5; // Minimum pixels to move before considering it a drag

  // Get current dashboard URL based on active tab
  const grafanaUrl = `${GRAFANA_BASE_URL}${DASHBOARDS[activeTab].path}`;

  // Sync internal state with external prop
  useEffect(() => {
    setIsSidebarOpen(isOpen);
  }, [isOpen]);

  // Always reset to default position on mount (no localStorage persistence)
  useEffect(() => {
    setButtonPosition(DEFAULT_POSITION);
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(true);
    setHasMoved(false);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !dragStart || !buttonRef.current) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      // Check if movement exceeds threshold
      if (
        !hasMoved &&
        (Math.abs(deltaX) > dragThreshold || Math.abs(deltaY) > dragThreshold)
      ) {
        setHasMoved(true);
      }

      if (
        hasMoved ||
        Math.abs(deltaX) > dragThreshold ||
        Math.abs(deltaY) > dragThreshold
      ) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate new position (from right and bottom)
        let newX = viewportWidth - (e.clientX + buttonRect.width / 2);
        let newY = viewportHeight - (e.clientY + buttonRect.height / 2);

        // Constrain to viewport boundaries
        newX = Math.max(0, Math.min(newX, viewportWidth - buttonRect.width));
        newY = Math.max(0, Math.min(newY, viewportHeight - buttonRect.height));

        setButtonPosition({ x: newX, y: newY });
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    },
    [isDragging, dragStart, hasMoved, dragThreshold],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);

    // Small delay to prevent click event if dragged
    if (hasMoved) {
      setTimeout(() => setHasMoved(false), 100);
    }
  }, [hasMoved]);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleOpenSidebar = () => {
    // Only open sidebar if not dragging
    if (!hasMoved) {
      setIsSidebarOpen(true);
    }
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    console.log(
      `Switching to dashboard tab: ${newValue} - ${DASHBOARDS[newValue].label}`,
    );
    setActiveTab(newValue);
  };

  return (
    <>
      {/* Draggable Circular Button - Only rendered if showFloatingButton is true */}
      {showFloatingButton &&
        ReactDOM.createPortal(
          <Fab
            ref={buttonRef}
            onClick={handleOpenSidebar}
            onMouseDown={handleMouseDown}
            className={`global-dashboard-button ${
              isDragging ? "dragging" : ""
            }`}
            sx={{
              position: "fixed",
              bottom: `${buttonPosition.y}px`,
              right: `${buttonPosition.x}px`,
              width: 56,
              height: 56,
              minWidth: 56,
              backgroundColor: "var(--auto-green) !important",
              color: "rgb(248, 248, 248)",
              zIndex: 9999,
              cursor: isDragging ? "grabbing" : "grab",
              userSelect: "none",
              boxShadow: isDragging
                ? "0 20px 25px rgba(0, 0, 0, 0.2), 0 10px 10px rgba(0, 0, 0, 0.1)"
                : "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
              transition: isDragging
                ? "none"
                : "box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "var(--auto-green) !important",
                color: "rgb(248, 248, 248)",
                boxShadow:
                  "0 10px 15px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)",
                transform: isDragging ? "none" : "translateY(-2px)",
                "& .button-icon": {
                  transform: "scale(1.1)",
                },
              },
              "&:active": {
                cursor: "grabbing",
                backgroundColor: "var(--auto-green) !important",
                color: "rgb(248, 248, 248)",
              },
              "& .button-icon": {
                fontSize: 28,
                color: "rgb(248, 248, 248)",
                transition: "transform 0.2s ease-in-out",
                transform: isDragging ? "scale(0.95)" : "scale(1)",
              },
            }}
            aria-label="Open Grafana Dashboard"
          >
            <MonitorHeartIcon className="button-icon" />
          </Fab>,
          document.body,
        )}

      {/* Sidebar Drawer - Also rendered via Portal */}
      {ReactDOM.createPortal(
        <Drawer
          anchor="right"
          open={isSidebarOpen}
          onClose={handleCloseSidebar}
          sx={{
            zIndex: 10000,
            "& .MuiDrawer-paper": {
              width: "75vw",
              boxSizing: "border-box",
              backgroundColor: theme.palette.background.paper,
            },
          }}
          transitionDuration={300}
        >
          {/* Sidebar Header with Tabs */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {/* Dashboard Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="secondary"
              indicatorColor="secondary"
              className="mui-tabs alternative-1"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="dashboard tabs"
              sx={{
                flex: 1,
                minHeight: 48,
                "& .MuiTab-root": {
                  minHeight: 48,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "16px",
                },
              }}
            >
              {DASHBOARDS.map((dashboard, index) => (
                <Tab
                  key={dashboard.id}
                  label={dashboard.label}
                  id={`dashboard-tab-${index}`}
                  aria-controls={`dashboard-tabpanel-${index}`}
                />
              ))}
            </Tabs>

            {/* Close Button */}
            <IconButton
              onClick={handleCloseSidebar}
              sx={{
                marginRight: 2,
                color: theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              aria-label="close sidebar"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Iframe Container */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
              height: "calc(100vh - 49px)", // Adjusted for header with tabs (49px)
              width: "100%",
            }}
          >
            <iframe
              title="Power BI Dashboard"
              src={grafanaUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              allow="fullscreen"
              loading="lazy"
              allowFullScreen
            />
          </Box>
        </Drawer>,
        document.body,
      )}
    </>
  );
};

export default GlobalDashboardButton;
